import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { LuMail, LuClock } from 'react-icons/lu';
import { CONTACT_EMAIL, trackMetaLead } from '../data/site';

export const NEED_OPTIONS = [
  'Sito',
  'Gestionale / CRM',
  'Sito e gestionale',
  'Gestione del sito già online',
  'Altro',
];

const HOME_NEED_OPTIONS = [
  'Sito nuovo',
  'Rifare il sito',
  'Gestione mensile',
  'Marketing',
  'AI e automazione',
  'Case vacanze',
  'Legale / fiscale',
  'Gare e appalti',
  'Altro',
];

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const ContactForm = ({
  title = 'Raccontaci cosa ti serve. Ti rispondiamo entro 24 ore.',
  subtitle,
  needOptions = HOME_NEED_OPTIONS,
  source = 'home',
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    activity: '',
    need: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const submitRef = useRef(null);

  useEffect(() => {
    const btn = submitRef.current;
    if (!btn) return;

    const syncChatYield = () => {
      if (window.innerWidth >= 768) {
        document.body.classList.remove('contact-cta-visible');
        return;
      }
      const rect = btn.getBoundingClientRect();
      const chatZoneTop = window.innerHeight - 72;
      const overlapsChat = rect.bottom > chatZoneTop && rect.top < window.innerHeight;
      document.body.classList.toggle('contact-cta-visible', overlapsChat);
    };

    syncChatYield();
    window.addEventListener('scroll', syncChatYield, { passive: true });
    window.addEventListener('resize', syncChatYield);
    return () => {
      window.removeEventListener('scroll', syncChatYield);
      window.removeEventListener('resize', syncChatYield);
      document.body.classList.remove('contact-cta-visible');
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const name = formData.name.trim();
      const phone = formData.phone.trim();
      const email = formData.email.trim();
      const activity = formData.activity.trim();
      const need = formData.need.trim();
      const note = formData.message.trim();

      if (!name || !phone || !activity || !need) {
        throw new Error('Compila i campi obbligatori');
      }

      if (email && !isEmail(email)) {
        throw new Error('Email non valida');
      }

      const composed = [
        `Tipo di attività: ${activity}`,
        `Cosa serve: ${need}`,
        `Cellulare: ${phone}`,
        `Email: ${email || '(non indicata)'}`,
        note ? `Messaggio: ${note}` : 'Messaggio: (nessun messaggio)',
      ].join('\n');

      const formDataToSend = new FormData();
      formDataToSend.append('first-name', name);
      formDataToSend.append('last-name', activity);
      formDataToSend.append('email', email);
      formDataToSend.append('phone', phone);
      formDataToSend.append('subject', need);
      formDataToSend.append('message', composed);
      formDataToSend.append('activity', activity);
      formDataToSend.append('contact', phone);
      formDataToSend.append('source', source);

      const response = await fetch('/send-mail.php', {
        method: 'POST',
        body: formDataToSend,
      });

      const raw = await response.text();
      let responseData = {};
      try {
        responseData = raw ? JSON.parse(raw) : {};
      } catch {
        throw new Error('Invio fallito');
      }

      if (!response.ok || responseData.status === 'error') {
        throw new Error(responseData.message || 'Invio fallito');
      }

      trackMetaLead();
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        activity: '',
        need: '',
        message: '',
      });
    } catch (error) {
      console.error('Errore invio:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contatti" className="section contact-section">
      <div className="container">
        <motion.div
          className="glass-panel contact-panel"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <header className="contact-header">
            <h2 className="text-gradient contact-title">
              {title}
            </h2>
            {subtitle && <p className="section-subtitle">{subtitle}</p>}
          </header>

          <div className="contact-form-meta">
            <div className="contact-meta">
              <h3>
                <LuMail className="text-accent" aria-hidden="true" /> Email
              </h3>
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </div>
            <p className="contact-sla">
              <LuClock aria-hidden="true" /> Risposta media: entro 24h
            </p>
          </div>

          <form onSubmit={handleSubmit} className="contact-form">
            {submitStatus === 'success' && (
              <div className="form-alert-success">
                Richiesta inviata. Ti rispondiamo entro 24 ore.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="form-alert-error">
                Invio non riuscito. Riprova o scrivici via email.
              </div>
            )}

            <div className="contact-fields">
              <div className="form-group">
                <label className="form-label" htmlFor="contact-name">Nome</label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  className="form-input"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="contact-phone">Cellulare</label>
                <input
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  className="form-input"
                  autoComplete="tel"
                  inputMode="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="contact-email">
                  Mail <span className="form-optional">(facoltativo)</span>
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  className="form-input"
                  autoComplete="email"
                  inputMode="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="contact-activity">Tipo di attività</label>
                <input
                  id="contact-activity"
                  type="text"
                  name="activity"
                  className="form-input"
                  placeholder="Es. ristorante, studio, negozio"
                  value={formData.activity}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group form-span">
                <label className="form-label" htmlFor="contact-need">Cosa ti serve</label>
                <select
                  id="contact-need"
                  name="need"
                  className="form-input"
                  value={formData.need}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>Seleziona</option>
                  {needOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="form-group form-span">
                <label className="form-label" htmlFor="contact-message">Messaggio <span className="form-optional">(facoltativo)</span></label>
                <textarea
                  id="contact-message"
                  name="message"
                  className="form-textarea"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
              </div>
            </div>

            <button
              ref={submitRef}
              type="submit"
              className="btn btn-primary contact-submit"
              style={{ width: '100%', minHeight: 44 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Invio in corso...' : 'Invia richiesta'}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
